import type { ExportResult } from "@opentelemetry/core";
import type { IOtlpExportDelegate } from "@opentelemetry/otlp-exporter-base";
import { OTLPExporterBase } from "@opentelemetry/otlp-exporter-base";
import {
  convertLegacyBrowserHttpOptions,
  createOtlpSendBeaconExportDelegate,
  createOtlpXhrExportDelegate,
} from "@opentelemetry/otlp-exporter-base/browser-http";
import { JsonTraceSerializer } from "@opentelemetry/otlp-transformer";
import type { ReadableSpan, SpanExporter } from "@opentelemetry/sdk-trace-base";

type BrowserExporterConfig = {
  url?: string;
  timeoutMillis?: number;
  // Intentionally omit headers; we embed auth in payload instead
  // headers?: Record<string, string>;
};

export class HeadersInBodyOTLPTraceExporter
  extends OTLPExporterBase<ReadableSpan[]>
  implements SpanExporter
{
  private readonly defaultDelegate: IOtlpExportDelegate<ReadableSpan[]>;
  private readonly beaconDelegate?: IOtlpExportDelegate<ReadableSpan[]>;
  private beaconModeUntil = 0;
  private visibilityListener?: () => void;
  private pageHideListener?: (event: Event) => void;
  private freezeListener?: () => void;
  private beforeUnloadListener?: (event: Event) => void;

  constructor(config: BrowserExporterConfig = {}, customHeaders?: Record<string, string>) {
    const serializer = {
      serializeRequest: (spans: ReadableSpan[]) => {
        const encoded = JsonTraceSerializer.serializeRequest(spans as unknown as ReadableSpan[]);
        if (!encoded) return encoded;
        try {
          const decoder = new TextDecoder();
          const body = JSON.parse(decoder.decode(encoded));
          const wrapped = { ...body, headers: customHeaders || {} };
          const encoderUtf8 = new TextEncoder();
          return encoderUtf8.encode(JSON.stringify(wrapped));
        } catch {
          // If anything fails, fall back to the original bytes so we don't block exports
          return encoded;
        }
      },
      deserializeResponse: (data: Uint8Array) => JsonTraceSerializer.deserializeResponse(data),
    };

    // Use the standard configuration pipeline so retries and queue handling stay consistent.
    const options = convertLegacyBrowserHttpOptions(config as any, "v1/traces", {
      "Content-Type": "application/json",
    });

    const defaultDelegate = createOtlpXhrExportDelegate(options, serializer as any);
    super(defaultDelegate as any);

    this.defaultDelegate = defaultDelegate as IOtlpExportDelegate<ReadableSpan[]>;
    this.beaconDelegate =
      typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function"
        ? (createOtlpSendBeaconExportDelegate(options, serializer as any) as IOtlpExportDelegate<
            ReadableSpan[]
          >)
        : undefined;

    this._setupBeaconGuards();
  }

  override export(spans: ReadableSpan[], resultCallback: (result: ExportResult) => void): void {
    this._getActiveDelegate().export(spans, resultCallback);
  }

  override forceFlush(): Promise<void> {
    return Promise.all([
      this.defaultDelegate.forceFlush(),
      this.beaconDelegate ? this.beaconDelegate.forceFlush() : Promise.resolve(),
    ]).then(() => void 0);
  }

  override async shutdown(): Promise<void> {
    this._teardownBeaconGuards();
    await Promise.all([
      this.defaultDelegate.shutdown(),
      this.beaconDelegate ? this.beaconDelegate.shutdown() : Promise.resolve(),
    ]);
  }

  private _setupBeaconGuards() {
    if (!this.beaconDelegate || typeof document === "undefined" || typeof window === "undefined") {
      return;
    }

    const markBeaconMode = () => {
      this.beaconModeUntil = Date.now() + 2000;
    };

    this.visibilityListener = () => {
      if (document.visibilityState === "hidden") {
        markBeaconMode();
      }
    };
    document.addEventListener("visibilitychange", this.visibilityListener, {
      capture: true,
      passive: true,
    } as any);

    this.pageHideListener = (event: Event) => {
      const pageEvent = event as PageTransitionEvent;
      if (!pageEvent.persisted) {
        markBeaconMode();
      }
    };
    window.addEventListener(
      "pagehide",
      this.pageHideListener as any,
      {
        capture: true,
        passive: true,
      } as any,
    );

    this.freezeListener = () => {
      markBeaconMode();
    };
    if ("onfreeze" in document) {
      document.addEventListener(
        "freeze",
        this.freezeListener as any,
        {
          capture: true,
          passive: true,
        } as any,
      );
    }

    this.beforeUnloadListener = () => {
      markBeaconMode();
    };
    window.addEventListener(
      "beforeunload",
      this.beforeUnloadListener as any,
      {
        capture: true,
      } as any,
    );
  }

  private _teardownBeaconGuards() {
    if (this.visibilityListener) {
      document.removeEventListener("visibilitychange", this.visibilityListener);
      this.visibilityListener = undefined;
    }
    if (this.pageHideListener && typeof window !== "undefined") {
      window.removeEventListener("pagehide", this.pageHideListener as any);
      this.pageHideListener = undefined;
    }
    if (this.freezeListener) {
      document.removeEventListener("freeze", this.freezeListener as any);
      this.freezeListener = undefined;
    }
    if (typeof window !== "undefined") {
      if (this.beforeUnloadListener) {
        window.removeEventListener("beforeunload", this.beforeUnloadListener as any);
        this.beforeUnloadListener = undefined;
      }
    }
  }

  private _getActiveDelegate(): IOtlpExportDelegate<ReadableSpan[]> {
    if (this.beaconDelegate && this._shouldUseBeacon()) {
      return this.beaconDelegate;
    }
    return this.defaultDelegate;
  }

  private _shouldUseBeacon() {
    if (!this.beaconDelegate) {
      return false;
    }
    if (this.beaconModeUntil && Date.now() < this.beaconModeUntil) {
      return true;
    }
    return false;
  }
}
